import * as React from "react";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { Button } from "./button";
import { cn } from "../../lib/utils";

const Pagination = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center justify-between py-4", className)}
    {...props}
  />
));
Pagination.displayName = "Pagination";

const PaginationContent = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center gap-1", className)}
    {...props}
  />
));
PaginationContent.displayName = "PaginationContent";

const PaginationItem = React.forwardRef(({ className, ...props }, ref) => (
  <div ref={ref} className={cn(className)} {...props} />
));
PaginationItem.displayName = "PaginationItem";

const PaginationPrevious = React.forwardRef(
  ({ className, disabled, onClick, ...props }, ref) => (
    <Button
      ref={ref}
      variant="ghost"
      size="sm"
      disabled={disabled}
      onClick={onClick}
      className={cn("h-8 w-8 p-0", className)}
      {...props}
    >
      <IconChevronLeft className="h-4 w-4" />
    </Button>
  )
);
PaginationPrevious.displayName = "PaginationPrevious";

const PaginationNext = React.forwardRef(
  ({ className, disabled, onClick, ...props }, ref) => (
    <Button
      ref={ref}
      variant="ghost"
      size="sm"
      disabled={disabled}
      onClick={onClick}
      className={cn("h-8 w-8 p-0", className)}
      {...props}
    >
      <IconChevronRight className="h-4 w-4" />
    </Button>
  )
);
PaginationNext.displayName = "PaginationNext";

const PaginationLink = React.forwardRef(
  ({ className, isActive, onClick, children, ...props }, ref) => (
    <Button
      ref={ref}
      variant={isActive ? "default" : "ghost"}
      size="sm"
      onClick={onClick}
      className={cn(
        "h-8 min-w-8 p-0 text-sm font-medium",
        !isActive && "text-text-secondary hover:text-text-primary",
        className
      )}
      {...props}
    >
      {children}
    </Button>
  )
);
PaginationLink.displayName = "PaginationLink";

const PaginationEllipsis = React.forwardRef(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn("flex h-8 w-8 items-center justify-center text-text-tertiary", className)}
    {...props}
  >
    ...
  </span>
));
PaginationEllipsis.displayName = "PaginationEllipsis";

const PaginationInfo = React.forwardRef(
  ({ className, currentPage, pageSize, totalItems, ...props }, ref) => {
    const start = (currentPage - 1) * pageSize + 1;
    const end = Math.min(currentPage * pageSize, totalItems);
    return (
      <p
        ref={ref}
        className={cn("text-sm text-text-tertiary", className)}
        {...props}
      >
        Showing {start}-{end} of {totalItems}
      </p>
    );
  }
);
PaginationInfo.displayName = "PaginationInfo";

const PaginationPageSize = React.forwardRef(
  (
    {
      className,
      value,
      onChange,
      options = [10, 20, 50, 100],
      ...props
    },
    ref
  ) => (
    <div ref={ref} className={cn("flex items-center gap-2", className)} {...props}>
      <span className="text-sm text-text-tertiary">Rows:</span>
      <select
        value={value}
        onChange={(e) => onChange?.(Number(e.target.value))}
        className="h-8 bg-surface-2 border border-glass-border text-sm text-text-primary px-2 focus:outline-none focus:ring-2 focus:ring-ring"
      >
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </div>
  )
);
PaginationPageSize.displayName = "PaginationPageSize";

export {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
  PaginationLink,
  PaginationEllipsis,
  PaginationInfo,
  PaginationPageSize,
};
